"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const inquiryTypes = [
  "General Inquiry",
  "Technical Support",
  "Business Partnership",
  "Feature Request",
  "Bug Report",
  "Billing Question",
  "Other",
];

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  submit?: string;
}

const initialForm: FormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export default function ContactForm() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
    name: false,
    email: false,
    subject: false,
    message: false,
  });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".scroll-fade-left")
              .forEach((el, i) => {
                setTimeout(() => el.classList.add("is-visible"), i * 90);
              });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validateField = (name: keyof FormState, value: string): string | undefined => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 2) return "Name must be at least 2 characters";
        break;
      case "email":
        if (!value.trim()) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Please enter a valid email address";
        break;
      case "subject":
        if (!value) return "Please select an inquiry type";
        break;
      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10) return "Message must be at least 10 characters";
        if (value.trim().length > 1000) return "Message must be less than 1000 characters";
        break;
    }
    return undefined;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name as keyof FormState]) {
      const error = validateField(name as keyof FormState, value);
      setErrors((prev) => ({ ...prev, [name]: error, submit: undefined }));
    }
  };

  const handleBlur = (name: keyof FormState) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, form[name]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    (Object.keys(form) as Array<keyof FormState>).forEach((key) => {
      const error = validateField(key, form[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    setTouched({ name: true, email: true, subject: true, message: true });

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    setErrors((prev) => ({ ...prev, submit: undefined }));

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        signal: AbortSignal.timeout(12000),
      });

      const data = await res.json();

      if (!res.ok) {
        let errorMsg = data?.error || "Failed to send your message. Please try again.";
        if (res.status === 408) {
          errorMsg = "Network timeout. Please check your connection and try again.";
        } else if (res.status === 422) {
          errorMsg = "The email address provided is invalid. Please check and try again.";
        } else if (res.status >= 500) {
          errorMsg = "Server error. Please try again in a moment.";
        }
        setErrors((prev) => ({ ...prev, submit: errorMsg }));
        return;
      }

      setSuccessMessage(
        data?.message ||
          `Your message has been sent! A confirmation has been delivered to ${form.email}. We'll respond within 24 hours.`
      );
      setSubmitted(true);
    } catch (err: any) {
      let errorMsg = "Something went wrong. Please try again.";
      if (err.name === "TimeoutError" || err.name === "AbortError") {
        errorMsg = "Network timeout. Please check your connection and try again.";
      } else if (!navigator.onLine) {
        errorMsg = "No internet connection. Please check your network and try again.";
      }
      setErrors((prev) => ({ ...prev, submit: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const getFieldClassName = (fieldName: keyof FormState) => {
    const baseClass = "form-input w-full";
    if (touched[fieldName] && errors[fieldName]) {
      return `${baseClass} border-red-400 focus:border-red-500 focus:ring-red-200`;
    }
    if (touched[fieldName] && !errors[fieldName] && form[fieldName]) {
      return `${baseClass} border-green-400 focus:border-green-500 focus:ring-green-200`;
    }
    return baseClass;
  };

  const renderFieldError = (fieldName: keyof FormState) => {
    if (touched[fieldName] && errors[fieldName]) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-red-600 mt-1">
          <Icon name="ExclamationCircleIcon" size={14} />
          <span>{errors[fieldName]}</span>
        </div>
      );
    }
    if (touched[fieldName] && !errors[fieldName] && form[fieldName]) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
          <Icon name="CheckCircleIcon" size={14} />
          <span>Looks good!</span>
        </div>
      );
    }
    return null;
  };

  return (
    <section ref={sectionRef} className="py-16 px-5 sm:px-8 lg:px-12 bg-white">
      <div className="max-w-xl mx-auto lg:mx-0 scroll-fade-left">
        {submitted ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
              <Icon name="CheckCircleIcon" size={40} className="text-teal-500" />
            </div>
            <h3 className="text-2xl font-bold text-[#1A2B2B] font-heading mb-3">
              Message sent successfully!
            </h3>
            <p className="text-[#4A6363] leading-relaxed mb-4">
              {successMessage}
            </p>
            <div className="flex flex-col gap-2 items-center mb-6">
              <div className="flex items-center gap-2 text-sm text-[#4A6363]">
                <Icon name="EnvelopeIcon" size={16} className="text-teal-500" />
                <span>Confirmation sent to {form.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#4A6363]">
                <Icon name="ClockIcon" size={16} className="text-teal-500" />
                <span>We typically respond within 24 hours</span>
              </div>
            </div>
            <button
              onClick={() => {
                setSubmitted(false);
                setSuccessMessage("");
                setForm(initialForm);
                setTouched({ name: false, email: false, subject: false, message: false });
                setErrors({});
              }}
              className="btn-primary"
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-[#1A2B2B] font-heading mb-3">
              Send us a message
            </h2>
            <p className="text-[#4A6363] leading-relaxed mb-8">
              Fill out the form below and we'll get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#1A2B2B] mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur("name")}
                  disabled={loading}
                  className={`${getFieldClassName("name")} ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                  placeholder="John Doe"
                  aria-invalid={touched.name && !!errors.name}
                />
                {renderFieldError("name")}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#1A2B2B] mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur("email")}
                  disabled={loading}
                  className={`${getFieldClassName("email")} ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                  placeholder="john@example.com"
                  aria-invalid={touched.email && !!errors.email}
                />
                {renderFieldError("email")}
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-[#1A2B2B] mb-2">
                  Inquiry Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  onBlur={() => handleBlur("subject")}
                  disabled={loading}
                  className={`${getFieldClassName("subject")} ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                  aria-invalid={touched.subject && !!errors.subject}
                >
                  <option value="">Select a topic...</option>
                  {inquiryTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {renderFieldError("subject")}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#1A2B2B] mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  onBlur={() => handleBlur("message")}
                  rows={6}
                  disabled={loading}
                  className={`${getFieldClassName("message")} resize-none ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                  placeholder="Tell us how we can help..."
                  aria-invalid={touched.message && !!errors.message}
                />
                <div className="flex items-center justify-between mt-1">
                  <div className="flex-1">{renderFieldError("message")}</div>
                  {form.message && (
                    <span
                      className={`text-xs ${
                        form.message.length > 1000 ? "text-red-600" : "text-[#8AA4A4]"
                      }`}
                    >
                      {form.message.length}/1000
                    </span>
                  )}
                </div>
              </div>

              {/* Submit error with retry */}
              {errors.submit && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700 font-medium">{errors.submit}</p>
                    <button
                      type="button"
                      onClick={() => setErrors((prev) => ({ ...prev, submit: undefined }))}
                      className="text-xs text-red-600 underline mt-1 hover:text-red-800 transition-colors"
                    >
                      Dismiss and try again
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Sending message...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={18} />
                    Send Message
                  </>
                )}
              </button>

              <div className="flex items-center gap-2 pt-2">
                <Icon name="LockClosedIcon" size={14} className="text-teal-500" />
                <p className="text-xs text-[#8AA4A4]">
                  Your information is encrypted and never shared with third parties.
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}